/**
 * useAssessmentCompletion Hook - Assessment Completion Detection
 * @module features/chat/hooks/useAssessmentCompletion
 *
 * Hook para detectar fim do assessment e gerenciar transicao para resultados.
 * Detecta marcador <!--ASSESSMENT_COMPLETED--> ou frases heuristicas.
 *
 * T002-FE: Deteccao de Fim do Assessment (US-007)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../stores/sessionStore';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Marcador HTML que indica fim do assessment (enviado pelo backend)
 */
export const COMPLETION_MARKER = '<!--ASSESSMENT_COMPLETED-->';

/**
 * Delay para exibir o overlay apos detectar fim (permite ler ultima mensagem)
 */
export const OVERLAY_DELAY_MS = 2500;

/**
 * Delay de transicao em ms apos exibir overlay antes de navegar para resultados
 */
export const TRANSITION_DELAY_MS = 2000;

/**
 * Frases heuristicas que indicam fim do assessment (fallback)
 * Case-insensitive matching
 */
export const HEURISTIC_PHRASES = [
  'tenho tudo o que preciso',
  'tenho todas as informacoes',
  'tenho todas as informações',
  'agora posso gerar',
  'vou gerar seu relatorio',
  'vou gerar seu relatório',
  'finalizando o review',
  'review concluido',
  'review concluído',
];

// =============================================================================
// TYPES
// =============================================================================

/**
 * Opcoes do hook useAssessmentCompletion
 */
export interface UseAssessmentCompletionOptions {
  /**
   * Callback chamado quando o assessment e completado
   * Chamado DEPOIS de completeSession() e ANTES da transicao
   * Executado em background (sem await)
   */
  onComplete?: () => void | Promise<void>;
}

/**
 * Retorno do hook useAssessmentCompletion
 */
export interface UseAssessmentCompletionReturn {
  /** Se o assessment foi completado */
  isCompleted: boolean;

  /** Se esta em transicao para resultados */
  isTransitioning: boolean;

  /** Verifica se uma mensagem indica fim do assessment */
  checkForCompletion: (content: string) => void;

  /** Remove o marcador de conclusao do conteudo */
  cleanContent: (content: string) => string;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook para detectar fim do assessment e gerenciar transicao
 *
 * @param options - Opcoes do hook
 * @param options.onComplete - Callback chamado quando assessment completa (executado em background)
 * @returns Estado e funcoes para gerenciar conclusao do assessment
 *
 * @example
 * ```tsx
 * const { isCompleted, isTransitioning, checkForCompletion, cleanContent } =
 *   useAssessmentCompletion({
 *     onComplete: () => {
 *       // Disparar geracao de relatorio em background
 *       generateReport(data).catch(console.error);
 *     }
 *   });
 *
 * // Verificar mensagem
 * checkForCompletion(message.content);
 *
 * // Limpar marcador antes de exibir
 * const displayContent = cleanContent(message.content);
 * ```
 */
export function useAssessmentCompletion(
  options: UseAssessmentCompletionOptions = {}
): UseAssessmentCompletionReturn {
  const { onComplete } = options;
  const navigate = useNavigate();
  const completeSession = useSessionStore((state) => state.completeSession);

  const [isCompleted, setIsCompleted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Refs para evitar multiplas chamadas
  const hasCompleted = useRef(false);
  const overlayTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * Verifica se o conteudo contem o marcador de conclusao
   */
  const hasCompletionMarker = useCallback((content: string): boolean => {
    return content.includes(COMPLETION_MARKER);
  }, []);

  /**
   * Verifica se o conteudo contem frases heuristicas de conclusao
   */
  const hasHeuristicPhrase = useCallback((content: string): boolean => {
    const normalizedContent = content.toLowerCase();
    return HEURISTIC_PHRASES.some((phrase) => normalizedContent.includes(phrase.toLowerCase()));
  }, []);

  /**
   * Verifica se uma mensagem indica fim do assessment
   */
  const checkForCompletion = useCallback(
    (content: string): void => {
      // Evitar multiplas execucoes
      if (hasCompleted.current) return;

      const isMarkerDetected = hasCompletionMarker(content);
      const isHeuristicDetected = hasHeuristicPhrase(content);

      if (isMarkerDetected || isHeuristicDetected) {
        hasCompleted.current = true;
        // FIX-006: Marcar como completado imediatamente para bloquear input
        setIsCompleted(true);

        // FIX-004: Executar callback PRIMEIRO (antes de completeSession)
        // Isso garante que generateReport() tenha acesso as mensagens do chat
        // antes que completeSession() potencialmente as modifique
        if (onComplete) {
          Promise.resolve(onComplete()).catch((error) => {
            console.error('[Assessment] onComplete callback failed:', error);
          });
        }

        // Marcar sessao como completa no store DEPOIS do callback
        completeSession();

        // FIX-006: Aguardar OVERLAY_DELAY_MS antes de mostrar o overlay
        // Isso permite que o usuario leia a ultima mensagem da IA
        overlayTimeoutRef.current = setTimeout(() => {
          setIsTransitioning(true);
        }, OVERLAY_DELAY_MS);
      }
    },
    [hasCompletionMarker, hasHeuristicPhrase, completeSession, onComplete]
  );

  /**
   * Remove o marcador de conclusao do conteudo para exibicao
   */
  const cleanContent = useCallback((content: string): string => {
    return content.replace(COMPLETION_MARKER, '').trim();
  }, []);

  /**
   * Efeito para navegar apos delay de transicao (apos overlay aparecer)
   */
  useEffect(() => {
    if (isTransitioning && !transitionTimeoutRef.current) {
      transitionTimeoutRef.current = setTimeout(() => {
        navigate('/results');
      }, TRANSITION_DELAY_MS);
    }

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [isTransitioning, navigate]);

  // Cleanup do overlay timeout
  useEffect(() => {
    return () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, []);

  return {
    isCompleted,
    isTransitioning,
    checkForCompletion,
    cleanContent,
  };
}

export default useAssessmentCompletion;
